import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Camera } from "@phosphor-icons/react";

const SHOTS = [
  { src: "/photos/volunteers-school.jpg", caption: "Paint Day at Gopanpally Thanda Government School", alt: "LLI volunteers in aprons outside Gopanpally Thanda Government School" },
  { src: "/photos/paint-prep.jpg", caption: "Mixing colours for a classroom makeover", alt: "Volunteers mixing paints before a school transformation day" },
  { src: "/photos/camp-group.jpg", caption: "Summer Camp smiles, supplies in hand", alt: "Students and volunteers holding up learning kits at Summer Camp" },
  { src: "/photos/team-students.jpg", caption: "A distribution drive with our partner school", alt: "LLI team standing before students during a distribution drive" },
  { src: "/photos/teacher-group.jpg", caption: "Our volunteer teachers, fresh from training", alt: "LLI volunteer teachers group photo after a training session" },
];

export const Gallery = () => {
  const trackRef = useRef(null);
  const scroll = (dir) => trackRef.current?.scrollBy({ left: dir * 420, behavior: "smooth" });

  return (
    <section className="py-24 lg:py-32 border-t border-forest/15 overflow-hidden" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-12 flex flex-wrap items-end justify-between gap-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <span className="text-xs font-bold tracking-[0.3em] uppercase text-terra flex items-center gap-2">
            <Camera size={16} weight="duotone" /> Day in the Field
          </span>
          <h2 className="font-serif font-bold tracking-tighter text-forest text-4xl sm:text-5xl lg:text-6xl mt-4" data-testid="gallery-heading">
            This is what your <span className="italic text-terra">rupee</span> does.
          </h2>
        </motion.div>
        <div className="flex gap-2">
          <button onClick={() => scroll(-1)} aria-label="Scroll gallery left" data-testid="gallery-prev"
            className="w-12 h-12 border border-forest/25 text-forest flex items-center justify-center hover:bg-forest hover:text-bone transition-colors duration-200">
            <ArrowLeft size={18} />
          </button>
          <button onClick={() => scroll(1)} aria-label="Scroll gallery right" data-testid="gallery-next"
            className="w-12 h-12 border border-forest/25 text-forest flex items-center justify-center hover:bg-forest hover:text-bone transition-colors duration-200">
            <ArrowRight size={18} />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="flex gap-5 overflow-x-auto snap-x snap-mandatory px-6 lg:px-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))] pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="gallery-track"
      >
        {SHOTS.map((shot, i) => (
          <motion.figure
            key={shot.src}
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: i * 0.07 }}
            className={`group relative shrink-0 snap-center overflow-hidden border border-forest/20 ${i % 2 === 0 ? "w-[78vw] sm:w-[420px]" : "w-[78vw] sm:w-[340px] sm:mt-10"}`}
            data-testid={`gallery-item-${i}`}
          >
            <img
              src={shot.src}
              alt={shot.alt}
              loading="lazy"
              className="w-full h-[300px] sm:h-[360px] object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-forest/90 via-forest/40 to-transparent p-5 pt-14">
              <span className="block font-serif italic text-bone text-base leading-snug">{shot.caption}</span>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
};
